import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { buildAsrModelOptions, DEFAULT_ASR_MODEL_VALUE } from '@helpers/asrProxy/buildAsrModelOptions';
import { getAsrModelCapabilities } from '@helpers/asrProxy/getAsrModelCapabilities';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { createFetchAsrResubmitModelOptionsQueryFn } from 'app/queries/asrProxy/fetchAsrResubmitModelOptions';
import { useState } from 'react';
import { createResubmitBulkAdminJobsMutationFn } from './AdminBulkResubmitJobsModal/api/resubmitBulkAdminJobs';
import { formatBulkResubmitJobResult } from './AdminBulkResubmitJobsModal/helpers/formatBulkResubmitJobResult';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  ids: string[];
};

export const AdminBulkResubmitJobsModal = ({ onCancel, onSuccess, ids }: TProps) => {
  const [priority, setPriority] = useState<string>('');
  const [clearChunksS3Cache, setClearChunksS3Cache] = useState<boolean>(false);
  const [transcriptionModel, setTranscriptionModel] = useState<string>(DEFAULT_ASR_MODEL_VALUE);
  const [useTwoStageClustering, setUseTwoStageClustering] = useState<'default' | 'enabled' | 'disabled'>('default');
  const [enhancedTranscriptMode, setEnhancedTranscriptMode] = useState<'auto' | 'force_asone' | 'force_assembly'>(
    'auto',
  );
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);

  const { call } = useAPI();
  const queryClient = useQueryClient();

  const modelOptionsQuery = useQuery({
    queryKey: asrProxyKeys.resubmitModelOptions(),
    queryFn: createFetchAsrResubmitModelOptionsQueryFn(call),
  });
  const asrProxyDisabled = modelOptionsQuery.data?.asrProxyDisabled ?? false;
  const proxyModels = modelOptionsQuery.data?.models ?? [];
  const defaultModel = modelOptionsQuery.data?.defaultModel ?? '';
  const proxyModelsLoading = modelOptionsQuery.isFetching;
  const selectedModel = proxyModels.find(
    (model) => model.name === (transcriptionModel === DEFAULT_ASR_MODEL_VALUE ? defaultModel : transcriptionModel),
  );
  const { supportsTwoStageClustering } = getAsrModelCapabilities(asrProxyDisabled ? undefined : selectedModel);
  const twoStageClusteringOverride = supportsTwoStageClustering
    ? useTwoStageClustering === 'enabled'
      ? true
      : useTwoStageClustering === 'disabled'
        ? false
        : undefined
    : undefined;
  const hasTranscriptionOverrides =
    !asrProxyDisabled && (transcriptionModel !== DEFAULT_ASR_MODEL_VALUE || twoStageClusteringOverride !== undefined);
  const resubmitMutation = useMutation({
    mutationFn: createResubmitBulkAdminJobsMutationFn(call),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: adminJobsKeys.reportLists(),
        }),
        queryClient.invalidateQueries({ queryKey: adminJobsKeys.details() }),
      ]);
    },
  });

  if (resubmitMutation.isSuccess) {
    const report = resubmitMutation.data;
    return (
      <ModalWrapper width="400px" onCancel={onSuccess}>
        <ModalTitle>Resubmission results</ModalTitle>
        {report ? (
          <>
            <p className="text-center" role="status">
              Resubmitted {report.results.filter((result) => result.status === 'resubmitted').length} of{' '}
              {report.totalFound} jobs.
            </p>
            <ul className="max-h-64 space-y-3 overflow-y-auto">
              {ids.map((id) => (
                <li key={id} className="break-words text-sm">
                  <span className="font-medium">{id}</span>:{' '}
                  {formatBulkResubmitJobResult(report.results.find((result) => result.jobId === id))}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p role="status" className="text-center">
            Resubmission request completed. Detailed results are unavailable. Check the refreshed job statuses before
            retrying.
          </p>
        )}
        <ModalFooter>
          <Button type="button" onClick={onSuccess}>
            Close
          </Button>
        </ModalFooter>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper width="400px">
      <ModalTitle>Confirm</ModalTitle>
      <div className="flex flex-col gap-6 text-center [&_small]:text-[var(--color-text-muted)]">
        <p>Resubmit the selected jobs? Jobs with No Show enabled will be skipped.</p>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {ids.map((id) => (
            <div key={id} className="text-sm p-2 border rounded">
              {id}
            </div>
          ))}
        </div>

        <div className="mb-8">
          <Select
            options={[
              { label: 'Yes', value: '1' },
              { label: 'No', value: '2' },
            ]}
            label="Clear chunks and metadata S3 cache"
            onChange={(_, value) => setClearChunksS3Cache(value === '1')}
            value={clearChunksS3Cache ? '1' : '2'}
          />
          <small className="text-[var(--color-text-muted)]">Clear chunks and metadata S3 cache</small>
        </div>

        <DisclosureSection
          title="Advanced options"
          open={advancedOpen}
          setOpenState={setAdvancedOpen}
          loading={false}
          titleClassName="!bg-[var(--color-surface)]"
        >
          <div className="mb-8 mt-6">
            <Select
              options={[
                { label: 'High', value: '1' },
                { label: 'Normal', value: '2' },
              ]}
              label={'Priority'}
              onChange={(_, value) => setPriority(value)}
              value={priority}
            />
            <small className="text-[var(--color-text-muted)]">
              Prioritizes this job at the front of the queue for processing
            </small>
          </div>

          {!asrProxyDisabled ? (
            <div className="mb-8">
              <Select
                options={buildAsrModelOptions({
                  models: proxyModels,
                  defaultModel,
                  selectedModel: transcriptionModel,
                })}
                label="ASR proxy model"
                onChange={(_, value) => setTranscriptionModel(value)}
                value={transcriptionModel}
                disabled={proxyModelsLoading}
              />
              <small style={{ color: 'var(--color-text-muted)' }}>
                {selectedModel && !selectedModel.healthy
                  ? `The last health check failed${selectedModel.healthDetail ? `: ${selectedModel.healthDetail}` : ''}`
                  : 'Applies to organizations on the ASR proxy; other jobs keep their default model'}
              </small>
            </div>
          ) : null}

          {supportsTwoStageClustering ? (
            <div className="mb-8">
              <Select
                options={[
                  { label: 'Default', value: 'default' },
                  { label: 'Yes', value: 'enabled' },
                  { label: 'No', value: 'disabled' },
                ]}
                label="Two-stage clustering"
                onChange={(_, value) => setUseTwoStageClustering(value as 'default' | 'enabled' | 'disabled')}
                value={useTwoStageClustering}
              />
              <small style={{ color: 'var(--color-text-muted)' }}>Enable two-stage clustering</small>
            </div>
          ) : null}

          <div className="mb-8">
            <Select
              options={[
                { label: 'Auto', value: 'auto' },
                { label: 'Force ASOne transcript.json', value: 'force_asone' },
                { label: 'Force asr.json', value: 'force_assembly' },
              ]}
              label="Enhanced transcript selection"
              onChange={(_, value) => setEnhancedTranscriptMode(value as 'auto' | 'force_asone' | 'force_assembly')}
              value={enhancedTranscriptMode}
            />
            <small className="text-[var(--color-text-muted)]">Select which transcript to enhance</small>
          </div>
        </DisclosureSection>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (resubmitMutation.isPending || proxyModelsLoading) {
              return;
            }
            resubmitMutation.mutate({
              requestBody: { ids },
              forceRetranscribe: hasTranscriptionOverrides,
              priority,
              clearStylefixCache: clearChunksS3Cache,
              chosenTranscriptionModel:
                !asrProxyDisabled && transcriptionModel !== DEFAULT_ASR_MODEL_VALUE ? transcriptionModel : undefined,
              useAssemblyTwoStageClustering: twoStageClusteringOverride,
              llmTranscriptSelectionMode: enhancedTranscriptMode,
            });
          }}
        >
          {resubmitMutation.isError ? (
            <ErrorMessage error={getApiErrorMessage(resubmitMutation.error) || 'Unable to resubmit the jobs.'} />
          ) : null}
          <ModalFooter>
            <OutlinedButton type="button" onClick={onCancel}>
              Cancel
            </OutlinedButton>
            <Button type="submit" disabled={resubmitMutation.isPending || proxyModelsLoading}>
              {resubmitMutation.isPending ? 'Resubmitting…' : 'Resubmit'}
            </Button>
          </ModalFooter>
        </form>
      </div>
    </ModalWrapper>
  );
};
